<div class="cr-page compose-page">
  <div class="cr-head">
    <div>
      <h1>Compose message</h1>
      <p>Goes to each taxpayer by email and into their portal inbox.</p>
    </div>
    <button mat-stroked-button type="button" class="cr-preview-toggle" (click)="togglePreview()" [attr.aria-pressed]="showPreview">
      <mat-icon>{{ showPreview ? 'visibility_off' : 'visibility' }}</mat-icon>{{ showPreview ? 'Hide preview' : 'Show preview' }}
    </button>
  </div>

  <mat-stepper #stepper linear class="compose-stepper" labelPosition="bottom">
    <!-- Step 1: Message -->
    <mat-step [stepControl]="messageForm" label="Message">
      <form [formGroup]="messageForm" class="step-content">
        <div class="cr-grid" [class.no-preview]="!showPreview">
          <div class="cr-card">
            <div class="cr-section">
              <div class="cr-label-row">
                <div class="cr-sec-title">Start from</div>
                <a mat-button [routerLink]="['/app/correspondence/templates']">Manage templates</a>
              </div>
              <div class="cr-options">
                <button type="button" class="cr-option" [attr.aria-pressed]="!selectedTemplate" (click)="startBlank()">
                  <strong><mat-icon>edit_note</mat-icon>Blank message</strong><span>Write from scratch</span>
                </button>
                @for (t of shownTemplates; track t.id) {
                  <button type="button" class="cr-option" [attr.aria-pressed]="selectedTemplate?.id === t.id" (click)="selectTemplate(t)" [title]="t.subject">
                    <strong><mat-icon>description</mat-icon>{{ t.name }}</strong><span>{{ t.subject }}</span>
                  </button>
                }
              </div>
              @if (templates.length > maxCards) {
                <mat-form-field appearance="outline" class="cr-full" subscriptSizing="dynamic">
                  <mat-label>Search all {{ templates.length }} templates</mat-label>
                  <mat-icon matPrefix>search</mat-icon>
                  <input matInput [(ngModel)]="templateSearchQuery" [ngModelOptions]="{standalone: true}" (ngModelChange)="applyTemplateFilter()"
                         [matAutocomplete]="tplAuto" placeholder="Name or subject">
                  <mat-autocomplete #tplAuto="matAutocomplete" (optionSelected)="selectTemplate($event.option.value); templateSearchQuery = ''; applyTemplateFilter()">
                    @for (t of filteredTemplates; track t.id) {
                      <mat-option [value]="t"><strong>{{ t.name }}</strong> <small class="muted">{{ t.subject }}</small></mat-option>
                    } @empty {
                      <mat-option disabled>No template matches “{{ templateSearchQuery }}”</mat-option>
                    }
                  </mat-autocomplete>
                </mat-form-field>
              }
            </div>
            <div class="cr-section">
              <mat-form-field appearance="outline" class="cr-full" subscriptSizing="dynamic">
                <mat-label>Subject</mat-label>
                <input matInput formControlName="subject" autocomplete="off">
                <mat-error>Subject is required</mat-error>
              </mat-form-field>
              <div class="cr-field">
                <div class="cr-label-row">
                  <span class="cr-label">Message</span>
                  <app-insert-variable [tokens]="tokens" (pick)="insertToken($event)"></app-insert-variable>
                </div>
                <app-html-editor #editor formControlName="body" [cidSource]="files" (addImage)="files?.openImagePicker()" (attachFile)="files?.openFilePicker()"
                  [invalid]="!!(messageForm.get('body')?.invalid && messageForm.get('body')?.touched)"></app-html-editor>
                @if (messageForm.get('body')?.hasError('required') && messageForm.get('body')?.touched) {
                  <mat-error>Message is required</mat-error>
                }
              </div>
              <app-correspondence-files [existing]="selectedTemplate?.attachments" (insertImage)="insertImage($event)"></app-correspondence-files>
            </div>
          </div>
          <aside class="cr-preview">
            <div class="cr-preview-head"><strong>Preview</strong><span class="cr-hint">Sample customer</span></div>
            <app-email-preview [subject]="messageForm.value.subject" [body]="messageForm.value.body" [cidUrls]="files?.cidUrls ?? {}"></app-email-preview>
            <p class="cr-hint">Customer info is filled in for each recipient when the message is sent.</p>
          </aside>
        </div>
        <div class="cr-actions">
          <button mat-button type="button" (click)="goBack()"><mat-icon>close</mat-icon>Cancel</button>
          <button mat-flat-button color="primary" matStepperNext [disabled]="messageForm.invalid">Next: recipients <mat-icon iconPositionEnd>arrow_forward</mat-icon></button>
        </div>
      </form>
    </mat-step>

    <!-- Step 2: Recipients -->
    <mat-step [stepControl]="audienceForm" label="Recipients">
      <form [formGroup]="audienceForm" class="step-content">
        <div class="cr-card"><div class="cr-section">
          <div class="cr-sec-title">Who should receive this?</div>
          <div class="cr-options">
            <button type="button" class="cr-option wrap" [attr.aria-pressed]="audienceType === 'ALL'" (click)="setAudience('ALL')">
              <strong><mat-icon>groups</mat-icon>Everyone</strong><span>Every account that is not closed</span>
            </button>
            <button type="button" class="cr-option wrap" [attr.aria-pressed]="audienceType === 'ACCOUNTS'" (click)="setAudience('ACCOUNTS')">
              <strong><mat-icon>person_search</mat-icon>Specific accounts</strong><span>Pick accounts by name or email</span>
            </button>
            <button type="button" class="cr-option wrap" [attr.aria-pressed]="audienceType === 'FILTER'" (click)="setAudience('FILTER')">
              <strong><mat-icon>filter_alt</mat-icon>Accounts that match…</strong><span>Type, status, location or registration date</span>
            </button>
          </div>
          @if (audienceType === 'ACCOUNTS') {
          <div class="filter-section">
            <mat-form-field appearance="outline" class="template-search-field">
              <mat-label>Find an account</mat-label>
              <mat-icon matPrefix>search</mat-icon>
              <input matInput [ngModel]="accountQuery" [ngModelOptions]="{standalone: true}" (ngModelChange)="onAccountQuery($event)"
                     [matAutocomplete]="auto" placeholder="Name or e-mail">
              <mat-autocomplete #auto="matAutocomplete" (optionSelected)="pickAccount($event.option.value)">
                @for (a of accountOptions; track a.id) {
                  <mat-option [value]="a"><strong>{{ a.name }}</strong> <small class="muted">{{ a.email }} · {{ a.type }} · {{ a.status }}</small></mat-option>
                }
              </mat-autocomplete>
            </mat-form-field>
            <mat-chip-set aria-label="Selected accounts">
              @for (a of pickedAccounts; track a.id) {
                <mat-chip (removed)="unpickAccount(a)">{{ a.name }} <small class="muted">({{ a.email || 'no e-mail' }})</small><button matChipRemove aria-label="Remove"><mat-icon>cancel</mat-icon></button></mat-chip>
              }
            </mat-chip-set>
            @if (pickedAccounts.length === 0) { <div class="muted">No accounts selected yet.</div> }
          </div>
        }

        @if (audienceType === 'FILTER') {
          <div class="filter-section">
            <div class="filter-row">
              <mat-form-field appearance="outline">
                <mat-label>Account Type</mat-label>
                <mat-select formControlName="accountType">
                  <mat-option [value]="null">All</mat-option>
                  @for (t of accountTypes; track t.value) { <mat-option [value]="t.value">{{ t.label }}</mat-option> }
                </mat-select>
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Account Status</mat-label>
                <mat-select formControlName="status">
                  <mat-option [value]="null">All (not closed)</mat-option>
                  @for (s of accountStatuses; track s.value) { <mat-option [value]="s.value">{{ s.label }}</mat-option> }
                </mat-select>
              </mat-form-field>
            </div>
            <div class="filter-row">
              <mat-form-field appearance="outline"><mat-label>City</mat-label><input matInput formControlName="city" placeholder="e.g. Miami"></mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>State</mat-label>
                <mat-select formControlName="state">
                  <mat-option value="">All</mat-option>
                  @for (s of states; track s) { <mat-option [value]="s">{{ s }}</mat-option> }
                </mat-select>
              </mat-form-field>
              <mat-form-field appearance="outline"><mat-label>Zip Code</mat-label><input matInput formControlName="zip" placeholder="e.g. 33101"></mat-form-field>
            </div>
            <div class="filter-row">
              <mat-form-field appearance="outline">
                <mat-label>Registered from</mat-label>
                <input matInput [matDatepicker]="fromPicker" formControlName="registeredFrom">
                <mat-datepicker-toggle matIconSuffix [for]="fromPicker"></mat-datepicker-toggle>
                <mat-datepicker #fromPicker></mat-datepicker>
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Registered to</mat-label>
                <input matInput [matDatepicker]="toPicker" formControlName="registeredTo">
                <mat-datepicker-toggle matIconSuffix [for]="toPicker"></mat-datepicker-toggle>
                <mat-datepicker #toPicker></mat-datepicker>
              </mat-form-field>
            </div>
          </div>
        }
          <div class="cr-count" [class.empty]="audienceCount === 0" aria-live="polite">
            <mat-icon>group</mat-icon>
            @if (audienceCountLoading) {
              <mat-spinner diameter="22"></mat-spinner><span>Counting recipients…</span>
            } @else {
              <span class="num">{{ audienceCount ?? '–' }}</span>
              <div><div>{{ audienceCount === 1 ? 'account will' : 'accounts will' }} receive this message</div><div class="cr-hint">{{ audienceDescription }}</div></div>
            }
          </div>
        </div></div>
        <div class="cr-actions">
          <button mat-button type="button" matStepperPrevious><mat-icon>arrow_back</mat-icon>Back</button>
          <button mat-flat-button color="primary" matStepperNext [disabled]="!audienceValid || !audienceCount">Next: review <mat-icon iconPositionEnd>arrow_forward</mat-icon></button>
        </div>
      </form>
    </mat-step>

    <!-- Step 3: Review & Send -->
    <mat-step [stepControl]="scheduleForm" label="Review &amp; Send">
      <form [formGroup]="scheduleForm" class="step-content">
        <div class="cr-grid" [class.no-preview]="!showPreview">
          <div class="cr-card"><div class="cr-section">
            <div class="cr-sec-title">Check before sending</div>
            <div class="cr-summary">
              <div>Subject</div><div>{{ messageForm.value.subject }}</div>
              <div>Recipients</div><div><b>{{ audienceCount }}</b> account(s) · {{ audienceDescription }}<a (click)="stepper.selectedIndex = 1">Change</a></div>
              <div>Started from</div><div>{{ selectedTemplate ? selectedTemplate.name : 'Blank message' }}</div>
              <div>Delivered to</div><div>Email and portal inbox</div>
            </div>
            <div class="cr-sec-title">When</div>
            <div class="cr-options">
              <button type="button" class="cr-option wrap" [attr.aria-pressed]="scheduleType === 'NOW'" (click)="setSchedule('NOW')">
                <strong><mat-icon>send</mat-icon>Send now</strong><span>Starts sending right away</span>
              </button>
              <button type="button" class="cr-option wrap" [attr.aria-pressed]="scheduleType === 'LATER'" (click)="setSchedule('LATER')">
                <strong><mat-icon>schedule</mat-icon>Schedule</strong><span>Pick a date and time</span>
              </button>
            </div>
            @if (scheduleType === 'LATER') {
              <div class="cr-row">
                <mat-form-field appearance="outline">
                  <mat-label>Date</mat-label>
                  <input matInput [matDatepicker]="schedulePicker" formControlName="scheduledDate">
                  <mat-datepicker-toggle matIconSuffix [for]="schedulePicker"></mat-datepicker-toggle>
                  <mat-datepicker #schedulePicker></mat-datepicker>
                  <mat-error>Pick a date</mat-error>
                </mat-form-field>
                <mat-form-field appearance="outline">
                  <mat-label>Time</mat-label>
                  <input matInput type="time" formControlName="scheduledTime">
                </mat-form-field>
              </div>
            }
          </div></div>
          <aside class="cr-preview">
            <div class="cr-preview-head"><strong>What recipients see</strong><span class="cr-hint">Sample customer</span></div>
            <app-email-preview [subject]="messageForm.value.subject" [body]="messageForm.value.body" [cidUrls]="files?.cidUrls ?? {}"></app-email-preview>
          </aside>
        </div>
        <div class="cr-actions">
          <button mat-button type="button" matStepperPrevious><mat-icon>arrow_back</mat-icon>Back</button>
          <button mat-flat-button color="primary" type="button" (click)="onSend()"
                  [disabled]="scheduleForm.invalid || sendLoading || !audienceCount || audienceCountLoading">
            @if (sendLoading) { <mat-spinner diameter="20"></mat-spinner> }
            @else { <mat-icon>{{ scheduleType === 'LATER' ? 'schedule' : 'send' }}</mat-icon>{{ scheduleType === 'LATER' ? 'Schedule for ' : 'Send to ' }}{{ audienceCount }} account(s) }
          </button>
        </div>
      </form>
    </mat-step>

    <!-- Step 4: Confirmation -->
    <mat-step label="Confirmation">
      <div class="step-content confirmation-step">
        <div class="confirmation-icon"><mat-icon class="success-icon">check_circle</mat-icon></div>
        <h2 class="confirmation-title">{{ sentStatus === 'SCHEDULED' ? 'Message Scheduled' : 'Message Queued' }}</h2>
        <p class="confirmation-subtitle">
          {{ sentStatus === 'SCHEDULED' ? 'It will be sent at the scheduled time.' : 'It is being sent in the background; watch the status under Sent Messages.' }}
        </p>
        <mat-card class="confirmation-summary-card">
          <mat-card-content>
            <div class="summary-row"><span class="summary-key">Subject</span><span class="summary-val">{{ messageForm.value.subject }}</span></div>
            <mat-divider></mat-divider>
            <div class="summary-row"><span class="summary-key">Recipients</span><span class="summary-val">{{ audienceCount }} account(s)</span></div>
            <mat-divider></mat-divider>
            <div class="summary-row"><span class="summary-key">Schedule</span>
              <span class="summary-val">@if (sentStatus === 'SCHEDULED') { {{ scheduledAt | date:'MM/dd/yyyy h:mm a' }} } @else { Sending now }</span>
            </div>
          </mat-card-content>
        </mat-card>
        <div class="confirmation-actions">
          <button mat-stroked-button type="button" (click)="startOver()"><mat-icon>add</mat-icon> Compose Another</button>
          <button mat-flat-button color="primary" type="button" [routerLink]="['/app/correspondence/emails']"><mat-icon>list</mat-icon> View Sent Messages</button>
        </div>
      </div>
    </mat-step>
  </mat-stepper>
</div>
